// =============================================================================
// shrinkImage — make a phone photo small enough for the AI to read.
//
// 8 Oct 2026. Larry's Officeworks docket came off the phone at 8.2 MB; as
// base64 that is 10.9 MB and the model refuses anything over 10 MB
// ("image exceeds 10 MB maximum"). The app's own cap was 10 MB on the FILE,
// which is 13 MB encoded — so a big-sensor phone could always get past us.
//
// A receipt does not need 48 megapixels. Anything the camera gives us is
// redrawn to at most LONG_EDGE pixels on its long side and saved as JPEG at
// QUALITY. That lands a typical docket at 300–900 KB: readable, fast to
// upload on one bar of signal, and well under the model's limit. PDFs and
// anything that fails to decode are returned untouched — the caller still
// gets a file, never an exception.
//
// EXIF orientation is honoured by createImageBitmap, so a portrait docket
// stays portrait. HEIC from iPhones usually arrives as JPEG already (Safari
// converts on the file input); if a raw HEIC ever gets through, decoding
// fails and the original is passed on as before.
// =============================================================================

export const LONG_EDGE = 2200;   // px — plenty for 7-pt docket print
export const QUALITY   = 0.85;
const SMALL_ENOUGH     = 1_500_000; // bytes — leave files under this alone

export async function shrinkImage(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  if (file.size <= SMALL_ENOUGH) return file;
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
    const scale = Math.min(1, LONG_EDGE / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale));
    const h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) { bmp.close(); return file; }
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close();
    const blob: Blob | null = await new Promise(res => canvas.toBlob(res, "image/jpeg", QUALITY));
    if (!blob || blob.size === 0 || blob.size >= file.size) return file;
    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg", lastModified: file.lastModified });
  } catch (e) {
    console.warn("[shrinkImage] left the original as-is", e);
    return file;
  }
}
